import { FormHelperText } from "@mui/material";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ReactHookCheckbox } from "core/components/ReactHookFormComponents/ReactHookCheckbox";
import { ReactHookEmailInput } from "core/components/ReactHookFormComponents/ReactHookEmailInput";
import {
  type PhoneCountryCode,
  ReactHookTelephoneInputV2,
} from "core/components/ReactHookFormComponents/ReactHookTelephoneInputV2";
import { ReactHookTextField } from "core/components/ReactHookFormComponents/ReactHookTextField";
import type { TFunction } from "core/entities";
import { useBrandName } from "core/hooks/useBrandName";
import type { EstimateRequestCustomerInformationFormValues } from "features/EstimateRequest/entities";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import { EstimateRequestShopSelect } from "./EstimateRequestShopSelect";

const NOTES_MAX_LENGTH = 500;

export function usePublicEstimateRequestCustomerInformationFormMethods() {
  const {
    query: { shopId },
  } = useRouter();
  return useForm<EstimateRequestCustomerInformationFormValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      notes: "",
      shopId: typeof shopId === "string" ? shopId.toLowerCase() : "",
      termsOfUseAgreement: false,
      maxShopDistance: typeof shopId === "string" ? "None" : 100,
    },
  });
}

type PublicEstimateRequestCustomerInformationFormProps = {
  formMethods: ReturnType<
    typeof usePublicEstimateRequestCustomerInformationFormMethods
  >;
  resetImages: () => void;
  t: TFunction;
};

export function PublicEstimateRequestCustomerInformationForm({
  formMethods,
  resetImages,
  t,
}: PublicEstimateRequestCustomerInformationFormProps) {
  const {
    query: { region },
  } = useRouter();
  const countryIso2AlphaCode =
    typeof region === "string" ? region.toUpperCase() : undefined;
  const brandName = useBrandName();

  const notes = formMethods.watch("notes");
  const notesLength = notes?.length ?? 0;

  const notesMaxLengthErrorMessage = t("maxLengthError", {
    maxLength: NOTES_MAX_LENGTH,
    currentLength: notesLength,
  });
  const notesHelperText = t("notesHelperText", {
    maxLength: NOTES_MAX_LENGTH,
    currentLength: notesLength,
  });

  return (
    <Box component="form">
      <Stack gap={2}>
        <ReactHookTextField
          required
          control={formMethods.control}
          name="firstName"
          autoComplete="given-name"
          label={t("firstName")}
        />
        <ReactHookTextField
          required
          control={formMethods.control}
          name="lastName"
          autoComplete="family-name"
          label={t("lastName")}
        />
        <ReactHookTelephoneInputV2
          required
          name="phoneNumber"
          control={formMethods.control}
          label={t("phoneNumber")}
          autoComplete="tel"
          country={countryIso2AlphaCode as PhoneCountryCode}
        />
        <ReactHookEmailInput
          required
          control={formMethods.control}
          name="email"
          label={t("email")}
        />
        <Stack>
          <ReactHookTextField
            control={formMethods.control}
            textFieldProps={{ placeholder: t("notesPlaceholder") }}
            name="notes"
            label={t("notes")}
            rules={{
              maxLength: {
                value: NOTES_MAX_LENGTH,
                message: notesMaxLengthErrorMessage,
              },
            }}
            multiline
            maxLength={NOTES_MAX_LENGTH}
            minRows={4}
          />
          <FormHelperText sx={{ textAlign: "right" }}>
            {notesHelperText}
          </FormHelperText>
        </Stack>
        <EstimateRequestShopSelect
          formMethods={formMethods}
          t={t}
          onChangeCallback={resetImages}
        />

        <Alert severity="info" icon={false}>
          <ReactHookCheckbox
            rules={{
              validate: (value) => value || t("termsOfUseAgreementError"),
            }}
            labelPlacement="end"
            control={formMethods.control}
            name="termsOfUseAgreement"
            label={t("termsOfUseAgreement")}
          />
          <Typography variant="caption">
            {t("termsOfUseAgreementDescription", { brandName })}
          </Typography>
        </Alert>
      </Stack>
    </Box>
  );
}
